<script setup lang="ts">
import type { RefDataModel } from '@rialtic/types'
import { useDebounceFn } from '@vueuse/core'
import FormFieldCell from './FormFieldCell.vue'
import { useClaimFormContext } from '../composables/fields'
import { useClaimLab } from '../stores/claimLab'
import type { WidgetAttributes } from '../utils/claim-editor'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  name: string
  attributes: WidgetAttributes
  label: string
  refDataModel?: RefDataModel
}>()

const claimLab = useClaimLab()
const { state, isFieldChanged } = useClaimFormContext()

const items = computed(() =>
  props.refDataModel
    ? claimLab
        .getAutocompleteItems(props.refDataModel)
        .map(({ value }) => value)
    : [],
)
/* The store tracks loading per ref data model, which every field of that model shares */
const pending = ref(0)
const loading = computed(() => pending.value > 0)

const searchStore = async (term: string) => {
  pending.value++

  try {
    await claimLab.searchAutocompleteStore(props.refDataModel!, term)
  } finally {
    pending.value--
  }
}

const text = computed(() => String(state[props.name] ?? ''))

const search = useDebounceFn(searchStore, 600)

/* Autocomplete mode: the typed text is the value, so free-form codes still reach validation */
const onUpdate = (value: string) => {
  state[props.name] = value

  if (props.refDataModel && value && isFieldChanged(props.name)) search(value)
}
</script>

<template>
  <FormFieldCell :name="props.name">
    <div
      v-if="attributes.readonly"
      class="flex items-center gap-1 p-2 text-xs"
      data-testid="FormAutocomplete__readonly"
    >
      {{ state[props.name] }}
      <UTooltip
        :text="`${label} is fixed for this claim type and cannot be changed.`"
        arrow
      >
        <UIcon name="i-ph-lock-fill" class="text-primary size-4" />
      </UTooltip>
    </div>
    <UInputMenu
      v-else
      v-bind="attributes"
      :name="props.name"
      class="w-full"
      :items="items"
      :loading="loading"
      :model-value="text"
      :ui="{ base: 'p-2 rounded-none' }"
      mode="autocomplete"
      open-on-focus
      size="xs"
      trailing
      variant="none"
      virtualize
      @update:model-value="onUpdate"
    >
      <template #empty="{ searchTerm }">
        {{ searchTerm ? 'No results' : 'Type to search' }}
      </template>
    </UInputMenu>
  </FormFieldCell>
</template>
